<script setup lang="ts">
import {Survey} from "@/core/interfaces/survey/Survey";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from "@/core/components/ui/dropdown-menu";
import BaseButton from "@/core/components/base/BaseButton.vue";
import SurveyCard from "@/survey/components/SurveyCard.vue";
import {ClipboardPlus, File, Microscope} from "@lucide/vue";
import {computed, inject, ref} from "vue";
import {Attachment} from "@/attachment/interfaces/attachment";
import AttachmentDialog from "@/attachment/components/AttachmentDialog.vue";
import {useRouter} from "vue-router";

const router = useRouter();
const emits = defineEmits<{ (e: 'surveyCompleted'): void; }>();
const props = defineProps<{
    surveys: Survey[];
    attachments: Attachment[];
    consumerKey: string;
}>();

const openSurveyModal = inject<(
    surveyId: number,
    consumerKey: string,
    pathwayStepId: number | null,
    supportProgramId: number | null,
    type?: string,
    options?: Record<string, unknown>
) => void>('openSurveyModal');

/**
 * Start the survey by opening the survey modal.
 */
const startSurvey = (survey: Survey) => {
    if (openSurveyModal && !survey.flowchart?.id) {
        openSurveyModal(
            survey.id,
            props.consumerKey,
            null,
            null,
            'survey',
            {
                showVerdict: true,
                onClose: handleSurveyCompleted,
                consent: survey.consent
            }
        );
    }

    if (survey.flowchart?.id) {
        router.push({name: 'flowcharts.show', params: {id: survey.flowchart!.id}});
    }
};

const selectedAttachmentIndex = ref<number | null>(null);

const sortedSurveys = computed(() => {
    return [...props.surveys].sort((a, b) => (a.theme ?? a.name).localeCompare((b.theme ?? b.name)));
});

const flowcharts = computed(() => {
    return sortedSurveys.value.filter((survey) => survey.flowchart && !survey.flowchart.laboratory_id);
});

/**
 * Handle the survey completed event.
 */
const handleSurveyCompleted = () => {
    emits('surveyCompleted');
};
</script>

<template>
  <div class="flex flex-col md:flex-row gap-3 md:items-center flex-wrap">
    <h1>
      Outils rapides
    </h1>
    <div class="flex items-center gap-3 flex-wrap">
      <DropdownMenu v-if="flowcharts?.length">
        <DropdownMenuTrigger as-child>
          <BaseButton density="comfortable">
            <Microscope class="w-4 h-4 mr-2" />
            Lancer un TROD
          </BaseButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="max-h-80 overflow-y-auto">
          <DropdownMenuItem
            v-for="flowchart in flowcharts"
            :key="flowchart.id"
            as-child
            @click="() => startSurvey(flowchart)"
          >
            <SurveyCard
              :survey="flowchart" :consumer-key="consumerKey"
              class="shadow-none hover:!bg-gray-100 transition-colors"
              @survey-completed="handleSurveyCompleted"
            />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu v-if="attachments?.length">
        <DropdownMenuTrigger as-child>
          <BaseButton density="comfortable">
            <File class="w-4 h-4 mr-2" />
            Préremplir un document
          </BaseButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="max-h-80 overflow-y-auto max-w-60">
          <DropdownMenuItem
            v-for="(attachment, index) in attachments" :key="attachment.id"
            class="cursor-pointer"
            @click="selectedAttachmentIndex = index"
          >
            {{ attachment.title }}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <BaseButton density="comfortable">
            <ClipboardPlus class="w-4 h-4 mr-2" />
            Questionnaire rapide
          </BaseButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="max-h-80 overflow-y-auto">
          <DropdownMenuItem
            v-for="survey in sortedSurveys.filter((_survey) => !_survey.flowchart)" :key="survey.id"
            as-child
            @click="() => startSurvey(survey)"
          >
            <SurveyCard
              :survey="survey" :consumer-key="consumerKey"
              class="shadow-none hover:!bg-gray-100 transition-colors"
              @survey-completed="handleSurveyCompleted"
            />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
    <AttachmentDialog
      v-if="selectedAttachmentIndex !== null"
      :open="true"
      :attachment="attachments[selectedAttachmentIndex]"
      :is-prefillable="true"
      @update:open="(val) => { if (!val) selectedAttachmentIndex = null }"
    />
  </div>
</template>

<style scoped>

</style>
